import {
  CheckCircleFilled,
  ImportOutlined, InboxOutlined, WarningOutlined
} from '@ant-design/icons';
import {
  Button, Card, message, Modal, Space, Spin, Typography,
  UploadProps
} from 'antd';
import { ColumnProps } from 'antd/es/table';
import { RcFile } from 'antd/es/upload';
import Dragger from 'antd/es/upload/Dragger';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation } from 'react-query';
import { useNavigate, useSearchParams } from 'react-router-dom';

import HeaderPage from 'common/components/HeaderPage';
import PageTable from 'common/components/PageTable';
import { getAccessToken } from 'common/services/common/storage';
import { importProjectStaffTemplateService } from 'common/services/extends/project';
import { StaffContractEnum } from 'common/services/extends/staff/types';
import { NoTaxCode, ROUTE_PATHS } from 'common/utils/constant';
import { downloadFile } from 'common/utils/functions';
import roles, { getPermission } from 'configs/roles';

type ImportTable = {
  key: number,
  nameCol: string,
  CCCDCol: string,
  taxCode?: string,
  contractTypeCol?: number,
  status: 'invalid' | 'valid',
  statusWarning?: 'invalid-warning' | 'valid',
  errorStatusCol?: Array<string>,
  warningStatusCol?: Array<string>,
  phoneNumber?: string,
};

const ProjectEmployeeImport: React.FC<ActiveRoles> = ({ roleOther }) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const projectIdParam = Number(searchParams.get('projectId'));
  const staffFullTimeId = searchParams.get('staffFullTimeId') || '';
  const payrollPeriodId = searchParams.get('payrollPeriodId') || '';
  const { t } = useTranslation();
  const [fileList, setFileList] = React.useState<RcFile | null>(null);

  const [jsonFile, setJsonFile] = React.useState<string>('');
  const [isLoadingJsonFile, setIsLoadingJsonFile] = React.useState<boolean>(false);
  const loadedJSonFileRef = React.useRef<boolean>(false);

  const canImportPermission = getPermission(roleOther, roles.EMPLOYEE_IMPORT);

  const { mutate: importProjectStaffTemplateMutate, isLoading } = useMutation(
    ['importProjectStaffTemplate'],
    () => importProjectStaffTemplateService(payrollPeriodId),
    {
      onSuccess: (data) => {
        if (data?.link) {
          downloadFile(data.link);
          message.success(t('message.exportSuccess'));
        }
      },
      onError: () => {
        message.error(t('message.exportError'));
      }
    }
  );

  const streamFile = async (fileRes: RcFile, preview: boolean) => {
    try {
      setIsLoadingJsonFile(true);
      const bodyForm = new FormData();
      bodyForm.append('file', fileRes);
      bodyForm.append('preview', preview ? '1' : '0');
      const token = getAccessToken();
      // request with form data to api for endpoint /api/v1/staffs/import
      const res = await fetch(`/api/v1/projects/payroll-period/${payrollPeriodId}/import-staff`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
        },
        body: bodyForm,
      });

      if (!res.ok) {
        console.log('Fail with status: ', res?.status);
        if (res.status === 401) {
          navigate('/login');
        } else {
          message.error(t('message.uploadError'));
        }
        setIsLoadingJsonFile(false);
        return;
      }
      const reader = res.body?.getReader();

      let result;
      const decoder = new TextDecoder();
      if (reader) {
        do {
          // eslint-disable-next-line no-await-in-loop
          result = await reader.read();
          const record = decoder.decode(result.value, { stream: true });
          if (preview) {
            setJsonFile((prev) => prev + record);
          }
        } while (!result.done);

        if (result.done) {
          loadedJSonFileRef.current = true;
          setIsLoadingJsonFile(false);
          if (!preview) {
            message.success(t('message.updateSuccess'));
            setFileList(null);
            setJsonFile('');
            navigate(`${ROUTE_PATHS.SALARY_PROGRAM_DETAIL}?projectId=${projectIdParam}&staffFullTimeId=${staffFullTimeId}&payrollPeriodId=${payrollPeriodId}`);
          }
        }
      }
    } catch (e) {
      setIsLoadingJsonFile(false);
      // Emtry
    }
  };

  const handleBeforeUpload = async (fileRes: RcFile): Promise<boolean> => {
    try {
      if (payrollPeriodId) {
        loadedJSonFileRef.current = false;
        setJsonFile('');
        setFileList(fileRes);
        streamFile(fileRes, true);
      }
    } catch {
      message.error('Import failed');
    }
    return false;
  };
  const uploadProps: UploadProps = {
    name: 'file',
    multiple: false,
    showUploadList: false,
    beforeUpload: handleBeforeUpload,
    disabled: isLoadingJsonFile,
  };

  const onCancel = () => {
    loadedJSonFileRef.current = true;
    setJsonFile('');
    setFileList(null);
  };

  const columns: ColumnProps<ImportTable>[] = [
    {
      title: t('contact.problem'),
      dataIndex: 'statusCol',
      key: 'statusCol',
      width: 380,
      fixed: 'left',
      sorter: {
        compare: (a: any, b: any) => a.status.localeCompare(b.status),
      },
      render: (text, record) => {
        if (record.status === 'valid' || record.statusWarning === 'valid') {
          return (
            <Space>
              <CheckCircleFilled style={{ color: '#52c41a' }} />
              <Typography.Text>Hợp lệ</Typography.Text>
            </Space>
          );
        }
        return (
          <ul style={{ paddingLeft: 0 }}>
            {
              record.errorStatusCol?.map((i, index) => (
                <li key={`col-status-${index.toString()}`} style={{ listStyle: 'none ' }}>
                  <Space size={12}>
                    <WarningOutlined style={{ color: '#f5222d' }} />
                    <Typography.Text>{i}</Typography.Text>
                  </Space>
                </li>
              ))
            }
            {
              record.warningStatusCol?.map((i, index) => (
                <li key={`col-status-${index.toString()}`} style={{ listStyle: 'none ' }}>
                  <Space size={12}>
                    <WarningOutlined style={{ color: '#f69320' }} />
                    <Typography.Text>{i}</Typography.Text>
                  </Space>
                </li>
              ))
            }
          </ul>
        );
      },
    },
    {
      title: t('system.name'),
      dataIndex: 'nameCol',
      key: 'nameCol',
      render: (text) => (
        <Typography.Text>{text}</Typography.Text>
      ),
    },
    {
      title: t('employee.contractType'),
      dataIndex: 'contractTypeCol',
      key: 'contractTypeCol',
      render: (text) => (
        <Typography.Text>{StaffContractEnum[text]}</Typography.Text>
      ),
    },
    {
      title: t('employee.identityNumber'),
      dataIndex: 'CCCDCol',
      key: 'CCCDCol',
      render: (text) => (
        <Typography.Text>{text}</Typography.Text>
      ),
    },
    {
      title: t('employee.taxNumber'),
      dataIndex: 'taxCode',
      key: 'taxCode',
      render: (text) => (
        <Typography.Text>{text}</Typography.Text>
      ),
    },
    {
      title: t('system.phone'),
      dataIndex: 'phoneNumber',
      key: 'phoneNumber',
      render: (text) => (
        <Typography.Text>{text}</Typography.Text>
      ),
    }
  ];

  const tableData = useMemo(() => {
    if (!loadedJSonFileRef.current) return null;
    if (jsonFile === '') return null;

    const lines = jsonFile.split('\n');
    const rowData: ImportTable[] = [];
    let totalData: number = 0;
    let errorsData: number = 0;
    lines.forEach((line) => {
      if (line.trim()) {
        const json = JSON.parse(line);
        if (json.status === 'processing') {
          const { row, total, errors } = json.data;

          if (!row) {
            if (Object.prototype.hasOwnProperty.call(json.data, 'total')) {
              totalData = total;
            }
            if (Object.prototype.hasOwnProperty.call(json.data, 'errors')) {
              errorsData = errors;
            }
            return;
          }

          const rowResult: ImportTable = {
            key: row?.rowNumber,
            nameCol: row?.fullname,
            contractTypeCol: row?.contractType,
            CCCDCol: row?.idCardNumber,
            phoneNumber: row?.phoneNumber,
            status: row?.errors?.length > 0 ? 'invalid' : 'valid',
            errorStatusCol: row?.errors,
            taxCode: row?.taxCode || NoTaxCode.label
          };
          rowData.push(rowResult);
        }
      }
    });

    return {
      total: totalData,
      errors: errorsData,
      rows: rowData
    };
  }, [jsonFile, loadedJSonFileRef.current]);

  return (
    <div className="p-employeeImport">
      <HeaderPage
        fixed
        title={t('sidebar.employeeImport')}
        rightHeader={(
          tableData && (
            <Space>
              <Button
                type="primary"
                disabled={!tableData.rows.length || tableData.errors > 0}
                onClick={() => {
                  Modal.confirm({
                    className: 't-pagetable_deleteLanguageModal',
                    autoFocusButton: 'cancel',
                    okText: t('system.ok'),
                    cancelText: t('system.cancel'),
                    cancelButtonProps: {
                      type: 'primary',
                    },
                    okButtonProps: {
                      type: 'default',
                    },
                    title: t('employee.importConfirm'),
                    onOk: () => {
                      if (fileList && payrollPeriodId) {
                        streamFile(fileList, false);
                      }
                    },
                  });
                }}
              >
                {t('system.ok')}
              </Button>
              <Button onClick={onCancel} disabled={isLoadingJsonFile}>
                {t('system.cancel')}
              </Button>
            </Space>
          ))}
      />
      <Spin spinning={isLoadingJsonFile}>
        <div className="t-mainlayout_wrapper">
          <Card
            className="p-employeeImport_card"
            title={(
              <Space style={{ width: '100%', justifyContent: 'space-between' }} direction="horizontal">
                {t('system.import')}
                {tableData ? null : (
                  <Button
                    type="primary"
                    disabled={isLoading || !canImportPermission}
                    onClick={() => importProjectStaffTemplateMutate()}
                    loading={isLoading}
                  >
                    <Space>
                      <ImportOutlined />
                      {t('system.downloadSampleFile')}
                    </Space>
                  </Button>
                )}
              </Space>
            )}
          >
            {tableData ? (
              <div>
                <div className="p-employeeImport_card_general">
                  <Space className="p-employeeImport_card_general-item" direction="vertical">
                    <Typography.Text className="p-employeeImport_card_general_title">Tổng số dòng dữ liệu</Typography.Text>
                    <Space className="p-employeeImport_card_general_wrapText">
                      <Typography.Text>{tableData.total}</Typography.Text>
                    </Space>
                  </Space>
                  <Space className="p-employeeImport_card_general-item success" direction="vertical">
                    <Typography.Text className="p-employeeImport_card_general_title">Số dòng hợp lệ</Typography.Text>
                    <Space className="p-employeeImport_card_general_wrapText">
                      <CheckCircleFilled style={{ color: '#52c41a' }} />
                      <Typography.Text>{tableData.total - tableData.errors}</Typography.Text>
                    </Space>
                  </Space>
                  <Space className="p-employeeImport_card_general-item error" direction="vertical">
                    <Typography.Text className="p-employeeImport_card_general_title">Số dòng lỗi</Typography.Text>
                    <Space className="p-employeeImport_card_general_wrapText">
                      <WarningOutlined style={{ color: '#f5222d' }} />
                      <Typography.Text>
                        {tableData.errors}
                      </Typography.Text>
                    </Space>
                  </Space>
                </div>
                <div className="p-employeeImport_card_table">
                  <PageTable
                    noCheckbox
                    isLoading={false}
                    tableProps={{
                      columns,
                      pageData: tableData.rows || [],
                      initShowColumns: ['statusCol', 'nameCol', 'contractTypeCol', 'CCCDCol', 'taxCode', 'phoneNumber'],
                      noBaseCol: true,
                      noDeleteLanguage: true,
                      rowClassName: (record) => (record.status === 'valid' ? 'p-employeeImport_card_table-validRow' : 'p-employeeImport_card_table-invalidRow'),
                    }}
                  />
                </div>
              </div>
            ) : (
              <Dragger {...uploadProps} disabled={isLoadingJsonFile || !canImportPermission}>
                <p className="ant-upload-drag-icon">
                  <InboxOutlined />
                </p>
                <p className="ant-upload-text">
                  {' '}
                  {t('media.dndText')}
                </p>
              </Dragger>
            )}
          </Card>
        </div>
      </Spin>
    </div>
  );
};
export default ProjectEmployeeImport;
